import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, Button } from '../components/ui'
import { authClient } from '../lib/auth-client'
import { fetchMe, ME_KEY, useMe } from '../lib/session'
import { SignInForm } from './sign-in'

export function AcceptInvitationPage({ invitationId }: { invitationId: string }) {
  const me = useMe()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const signedIn = me.data !== null && me.data !== undefined

  const invitation = useQuery({
    queryKey: ['invitation', invitationId],
    enabled: signedIn,
    retry: false,
    queryFn: async () => {
      const res = await authClient.organization.getInvitation({ query: { id: invitationId } })
      if (res.error) throw new Error(res.error.message ?? 'Invitation not found')
      return res.data
    },
  })

  const accept = useMutation({
    mutationFn: async () => {
      const res = await authClient.organization.acceptInvitation({ invitationId })
      if (res.error) throw new Error(res.error.message ?? 'Could not accept invitation')
      return res.data
    },
    onSuccess: async (data) => {
      const fresh = await qc.fetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 0 })
      const dealer = fresh?.dealers.find((d) => d.organization_id === data.invitation.organizationId)
      if (dealer) void navigate({ to: '/dealers/$dealerId', params: { dealerId: dealer.dealer_id } })
      else void navigate({ to: '/' })
    },
  })

  return (
    <section className="mx-auto max-w-sm space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-neutral-900">Join a dealership</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Sign in with the email address the invitation was sent to, then accept below.
        </p>
      </div>

      {me.data === null && <SignInForm redirectTo={`/accept-invitation/${invitationId}`} />}

      {signedIn && invitation.isPending && <p className="text-sm text-neutral-500">Loading invitation…</p>}
      {signedIn && invitation.isError && (
        <Alert tone="error">
          This invitation is not available for <strong>{me.data?.user.email}</strong>: it may have expired, been
          cancelled, or been sent to a different address.
        </Alert>
      )}
      {signedIn && invitation.data && (
        <div className="space-y-3 rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm p-4 text-sm">
          <p>
            <strong>{invitation.data.organizationName}</strong> invited <strong>{invitation.data.email}</strong> as{' '}
            <strong>{invitation.data.role}</strong>.
          </p>
          <p className="text-xs text-neutral-500">Expires {new Date(invitation.data.expiresAt).toLocaleString()}</p>
          <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
            Accept invitation
          </Button>
          {accept.isError && <Alert tone="error">{accept.error.message}</Alert>}
        </div>
      )}
    </section>
  )
}
